import { useEffect, useRef, useState } from "react";
import { loadGoogleGsiScript, resetGoogleGsiLoader } from "@/lib/googleGsi";

type Intent = "login" | "signup";

async function sleep(ms: number): Promise<void> {
  await new Promise<void>(r => {
    window.setTimeout(r, ms);
  });
}

/** Extra polls after script load — rare races where `accounts.id` is still undefined for a tick. */
async function waitForGoogleIdReady(maxAttempts = 12, delayMs = 48): Promise<boolean> {
  for (let i = 0; i < maxAttempts; i++) {
    if (window.google?.accounts?.id) return true;
    await sleep(delayMs);
  }
  return Boolean(window.google?.accounts?.id);
}

export function GoogleGsiButton({
  clientId,
  intent,
  onCredential,
  disabled,
  className = "",
}: {
  clientId: string;
  intent: Intent;
  onCredential: (jwt: string) => void;
  disabled?: boolean;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cbRef = useRef(onCredential);
  cbRef.current = onCredential;
  const [loadHint, setLoadHint] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!clientId.trim()) return;
    const el = containerRef.current;
    if (!el) return;

    let cancelled = false;
    setLoadHint(null);

    void (async () => {
      try {
        await loadGoogleGsiScript();
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : "";
          const timedOut = msg.includes("did not become ready");
          setLoadHint(
            timedOut
              ? "Google sign-in is taking longer than usual. You can try again below or refresh the page."
              : "Google sign-in could not load. Try again, check that extensions are not blocking Google, or refresh the page.",
          );
        }
        return;
      }
      if (cancelled || !containerRef.current) return;

      const ready = (await waitForGoogleIdReady()) && Boolean(window.google?.accounts?.id);
      if (!ready) {
        if (!cancelled) {
          setLoadHint(
            "Google sign-in did not finish loading. Try again or refresh the page.",
          );
        }
        return;
      }

      const g = window.google!.accounts!.id;

      el.innerHTML = "";
      g.initialize({
        client_id: clientId,
        callback: (res: { credential?: string }) => {
          const cred = typeof res.credential === "string" ? res.credential : "";
          if (cred) cbRef.current(cred);
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      g.renderButton(el, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: intent === "signup" ? "signup_with" : "signin_with",
        shape: "rectangular",
        logo_alignment: "left",
        width: 320,
      });
    })();

    return () => {
      cancelled = true;
      try {
        window.google?.accounts?.id?.cancel();
      } catch {
        /* ignore */
      }
      el.innerHTML = "";
    };
  }, [clientId, intent, retryKey]);

  return (
    <div
      className={`w-full flex flex-col items-center gap-2 ${className} ${disabled ? "pointer-events-none opacity-40" : ""}`}
      aria-disabled={disabled || undefined}
    >
      <div ref={containerRef} className="w-full flex justify-center min-h-[44px]" />
      {loadHint ? (
        <div className="flex w-full max-w-[320px] flex-col items-center gap-2 px-1">
          <p className="text-center text-[11px] text-white/50 font-mono leading-snug">
            {loadHint}
          </p>
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              resetGoogleGsiLoader();
              setLoadHint(null);
              setRetryKey(k => k + 1);
            }}
            className="text-[11px] font-mono font-semibold text-amber-300/90 underline underline-offset-2 hover:text-amber-200 disabled:opacity-40 disabled:no-underline"
          >
            Try loading Google sign-in again
          </button>
        </div>
      ) : null}
    </div>
  );
}
